import { RootState, INITIAL_STATE, Task, TaskDraft } from "@/core/state-shape";
import { http } from "@/lib/http";
import { useContextBundlesStore } from "@/stores/contextBundles";
import { createProjectScopedStore } from "./projectScoped";
import { measure } from "@/lib/performance";

import { toast } from "sonner";
export type TasksState = RootState["tasks"];
type StoreState = TasksState & { loading: boolean };

type TasksActions = {
  setState: (newState: StoreState) => void; // Add this action
  patch: (p: Partial<StoreState>) => void;
  reset: () => void;
  create: (draft: any, draftId: string) => Promise<string>;
  loadTasks: (projectId: string) => Promise<void>;
  loadTask: (taskId: string) => Promise<void>;
  select: (taskId: string | null) => void;
  setFilter: (filter: Partial<TasksState["uiPrefs"]["filters"]>) => void;
  setSort: (sort: TasksState["uiPrefs"]["sort"]) => void;
  setActiveDraft: (draftId: string | null) => void;
  toggleSelection: (taskId: string) => void;
  clearSelection: () => void;
  reopen: (taskId: string) => Promise<void>;
  createFollowUp: (sourceTaskId: string) => Promise<string>;
  closeTask: (taskId: string) => Promise<void>;
};

export const useTasksStore = createProjectScopedStore<StoreState & TasksActions>('tasks', (set, get) => ({
  ...INITIAL_STATE.tasks,
  loading: false,
  setState: (newState) => set(newState), // Add the implementation
  patch: (p) => set(p),
  reset: () => set({ ...INITIAL_STATE.tasks, loading: false }),

  create: measure(async (draft, draftId) => {
    try {
      const res = await http<{ taskId: string; status: string; createdAt: string }>(`/api/tasks`, {
        method: "POST",
        body: JSON.stringify(draft),
      });
      await get().loadTask(res.taskId);

      // Clear the used draft and set the new active task ID
      const drafts = { ...get().drafts };
      delete drafts[draftId];
      const newDraftId = `workspace-draft-${Date.now()}`;
      set({ activeTaskId: res.taskId, drafts, activeDraftId: newDraftId });

      toast.success('Task created successfully!');
      return res.taskId;
    } catch (e) {
      console.error("Failed to create task", e);
      toast.error('Failed to create task. Please try again.');
      throw e;
    }
  }, 'tasks.create'),

  loadTasks: measure(async (projectId) => {
    set({ loading: true });
    try {
      const {tasks} = await http<{tasks: Task[]}>(`/api/tasks?project_id=${encodeURIComponent(projectId)}`);
      console.log(tasks);
      const byId: Record<string, Task> = {};
      tasks.forEach(t => byId[t.id] = t);
      set({ byId, list: tasks.map(t => t.id), loading: false });
    } catch (e: any) {
      console.error("Failed to load tasks", e);
      toast.error("Failed to load tasks", {
        description: e.message
      });
      set({ loading: false });
    }
  }, 'tasks.loadTasks'),

  loadTask: measure(async (taskId) => {
    const task = await http<any>(`/api/tasks/${encodeURIComponent(taskId)}`);
    const byId = { ...(get().byId || {}) };
    byId[taskId] = task;
    const list = get().list?.includes(taskId) ? get().list : [taskId, ...(get().list || [])];
    set({ byId, list, activeTaskId: taskId } as Partial<TasksState>);
  }, 'tasks.loadTask'),

  select: (taskId) => set({ activeTaskId: taskId } as Partial<TasksState>),

  setFilter: (filter) =>
    set((state) => ({
      uiPrefs: {
        ...state.uiPrefs,
        filters: { ...state.uiPrefs.filters, ...filter },
      },
    })),

  setSort: (sort) =>
    set((state) => ({
      uiPrefs: { ...state.uiPrefs, sort },
    })),

  setActiveDraft: (draftId) => set({ activeDraftId: draftId } as Partial<TasksState>),
  toggleSelection: (taskId: string) =>
    set((state) => {
      const selection = new Set(state.uiPrefs.selection);
      if (selection.has(taskId)) {
        selection.delete(taskId);
      } else {
        selection.add(taskId);
      }
      return {
        uiPrefs: { ...state.uiPrefs, selection: Array.from(selection) },
      };
    }),

  clearSelection: () =>
    set((state) => ({
      uiPrefs: {
        ...state.uiPrefs,
        selection: [],
      },
    })),

  reopen: measure(async (taskId: string) => {
    const originalTask = get().byId[taskId];
    try {
      await http<void>(`/api/tasks/${taskId}/reopen`, {
        method: 'POST',
      });
      await get().loadTask(taskId);
      toast.success(`Task "${originalTask.title || taskId}" has been reopened.`);
    } catch (e: any) {
      // No need to rollback optimistic update as we are refetching state on any action.
      toast.error("Failed to reopen task", { description: e.message });
    }
  }, 'tasks.reopen'),

  closeTask: measure(async (taskId: string) => {
    const originalTask = get().byId[taskId];
    try {
      await http<void>(`/api/tasks/${taskId}/close`, {
        method: 'POST',
      });
      await get().loadTask(taskId);
      toast.success(`Task "${originalTask?.title || taskId}" has been closed.`);
    } catch (e: any) {
      toast.error("Failed to close task", { description: e.message });
      // Refetch state to ensure consistency after a failed action
      // The websocket will provide updates.
    }
  }, 'tasks.closeTask'),

  createFollowUp: measure(async (sourceTaskId) => {
    const sourceTask = get().byId[sourceTaskId];
    if (!sourceTask) {
        throw new Error("Source task not found for follow-up.");
    }

    const contextBundleId = sourceTask.contextBundleId;
    let scope: TaskDraft['scope'] = [];
    if (contextBundleId) {
        const bundle = useContextBundlesStore.getState().byId[contextBundleId];
        if (bundle) {
            scope = bundle.items.map(item => ({
                type: 'file', // Simplified for now as per ContextItem type
                path: item.ref
            }));
        }
    }

    const newDraftId = `workspace-draft-followup-${Date.now()}`;
    const newDraft: TaskDraft = {
        scope,
        intent: sourceTask.intent,
        instruction: `Follow-up to: "${sourceTask.title || sourceTask.id}"\n\n[Describe the new change here]`,
    };

    set(state => ({
        drafts: {
            ...state.drafts,
            [newDraftId]: newDraft
        },
                activeDraftId: newDraftId
    }));
    
    return newDraftId;
  }, 'tasks.createFollowUp'),
}));




